import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Microphone, MicrophoneMute, Xmark, Check } from "iconoir-react";
import { voiceOnboard } from "@/lib/api";
import { toast } from "sonner";

const PROMPTS = [
    "What do you want to eat this week?",
    "Who's cooking which day?",
    "Any chores to plan?",
    "Anything to add to the shopping list?",
];

export default function VoiceOnboarding({ session, onClose, onDone }) {
    const [phase, setPhase] = useState("intro"); // intro | listening | processing | done
    const [transcript, setTranscript] = useState("");
    const [interim, setInterim] = useState("");
    const [summary, setSummary] = useState("");
    const [actions, setActions] = useState([]);
    const recRef = useRef(null);

    const hasSpeech =
        typeof window !== "undefined" &&
        (window.SpeechRecognition || window.webkitSpeechRecognition);

    useEffect(() => {
        return () => {
            recRef.current?.stop?.();
        };
    }, []);

    const startListening = () => {
        if (!hasSpeech) {
            toast.error("Your browser doesn't support voice. Use the chat tab instead.");
            return;
        }
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const rec = new SR();
        rec.lang = "en-US";
        rec.interimResults = true;
        rec.continuous = true;
        let finalT = "";
        rec.onresult = (e) => {
            let it = "";
            for (let i = e.resultIndex; i < e.results.length; i++) {
                const r = e.results[i];
                if (r.isFinal) finalT += r[0].transcript + " ";
                else it += r[0].transcript;
            }
            setTranscript(finalT.trim());
            setInterim(it);
        };
        rec.onerror = (e) => {
            console.warn("speech error", e.error);
            const map = {
                "not-allowed": "Mic blocked. If you're inside a preview panel, open the URL in a new tab and allow mic access.",
                "service-not-allowed": "Mic blocked by browser/OS settings.",
                "no-speech": null,
                "aborted": null,
                "audio-capture": "No microphone found on this device.",
                "network": "Network error reaching the speech service.",
            };
            const msg = map[e.error];
            if (msg === undefined) toast.error("Mic error: " + e.error);
            else if (msg) toast.error(msg);
        };
        rec.onend = () => {
            // if still listening, restart
            if (phase === "listening") {
                try {
                    rec.start();
                } catch (e) { console.warn("components/VoiceOnboarding.jsx:", e); }
            }
        };
        rec.start();
        recRef.current = rec;
        setPhase("listening");
    };

    const stopAndProcess = async () => {
        try {
            recRef.current?.stop();
        } catch (e) { console.warn("components/VoiceOnboarding.jsx:", e); }
        const fullText = (transcript + " " + interim).trim();
        if (!fullText) {
            toast.error("I didn't catch anything. Try again?");
            setPhase("intro");
            return;
        }
        setPhase("processing");
        try {
            const res = await voiceOnboard(session.member_id, fullText);
            const acts = res.parsed_actions || [];
            // Single-action commands → quick toast + close. No overwhelming panel.
            if (acts.length <= 1) {
                toast.success(res.reply || "Done.");
                onDone?.();
                return;
            }
            setSummary(res.reply || "");
            setActions(acts);
            setPhase("done");
        } catch (e) {
            toast.error("Milo couldn't process that. Try again.");
            setPhase("intro");
        }
    };

    const typeText = (fullText) => {
        setTranscript(fullText);
        setInterim("");
        setPhase("done-manual");
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#FBF6EB]/95 backdrop-blur-sm z-50 flex flex-col p-6 overflow-y-auto"
            data-testid="voice-onboarding-modal"
        >
            <div className="max-w-2xl w-full mx-auto flex flex-col flex-1">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <img src="/milo-logo.png" alt="Milo" className="w-8 h-8 object-contain" />
                        <div className="font-heading font-black">Plan your week with voice</div>
                    </div>
                    <button
                        data-testid="close-voice-btn"
                        onClick={onClose}
                        className="w-10 h-10 brut-border rounded-lg bg-white flex items-center justify-center"
                    >
                        <Xmark className="w-5 h-5" />
                    </button>
                </div>

                {phase === "intro" && (
                    <div className="flex-1 flex flex-col items-center justify-center text-center gap-6">
                        <div className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">
                            Tell me about your week.
                        </div>
                        <ul className="text-sm sm:text-base text-zinc-700 max-w-md space-y-1">
                            {PROMPTS.map((p) => (
                                <li key={p} className="font-medium">
                                    · {p}
                                </li>
                            ))}
                        </ul>
                        <button
                            data-testid="start-voice-btn"
                            onClick={startListening}
                            className="brut-btn brut-btn-accent text-lg px-8 py-4 rounded-2xl"
                        >
                            <Microphone className="w-6 h-6" /> Start speaking
                        </button>
                        <details className="text-xs text-zinc-600">
                            <summary className="cursor-pointer">No mic? Type it instead</summary>
                            <TypeInput onSubmit={(t) => { setTranscript(t); stopAndProcessFromText(t); }} />
                        </details>
                    </div>
                )}

                {phase === "listening" && (
                    <div className="flex-1 flex flex-col items-center justify-center gap-6">
                        <div className="relative">
                            <div className="absolute inset-0 rounded-full bg-[#2F6B43] voice-pulse-ring" />
                            <div className="relative w-28 h-28 rounded-full bg-[#2F6B43] brut-border brut-shadow-lg flex items-center justify-center">
                                <Microphone className="w-12 h-12" />
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="text-xs uppercase tracking-widest font-bold text-zinc-600 mb-2">
                                Listening...
                            </div>
                            <div className="max-w-xl brut-card p-4 min-h-[4rem] text-left">
                                <span className="font-medium">{transcript}</span>{" "}
                                <span className="text-zinc-500 italic">{interim}</span>
                            </div>
                        </div>
                        <button
                            data-testid="stop-voice-btn"
                            onClick={stopAndProcess}
                            className="brut-btn text-base"
                        >
                            <MicrophoneMute className="w-5 h-5" /> I'm done — process it
                        </button>
                    </div>
                )}

                {phase === "processing" && (
                    <div className="flex-1 flex flex-col items-center justify-center gap-4">
                        <div className="flex gap-2">
                            <span className="w-3 h-3 rounded-full bg-[#0D2A1B] animate-bounce" />
                            <span className="w-3 h-3 rounded-full bg-[#0D2A1B] animate-bounce" style={{ animationDelay: "120ms" }} />
                            <span className="w-3 h-3 rounded-full bg-[#0D2A1B] animate-bounce" style={{ animationDelay: "240ms" }} />
                        </div>
                        <div className="font-heading text-xl">Milo is organizing...</div>
                    </div>
                )}

                {phase === "done" && (
                    <div className="flex-1 flex flex-col gap-4">
                        <div>
                            <div className="text-xs uppercase tracking-widest font-bold text-zinc-600">
                                Captured
                            </div>
                            <div className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
                                {summary || "Your week is set."}
                            </div>
                        </div>
                        {transcript && (
                            <div className="brut-card p-4 text-sm text-zinc-700">
                                <div className="text-xs uppercase tracking-widest font-bold mb-2">
                                    You said
                                </div>
                                {transcript}
                            </div>
                        )}
                        {actions.length > 0 && (
                            <div className="brut-card p-4">
                                <div className="text-xs uppercase tracking-widest font-bold mb-3">
                                    Actions taken ({actions.length})
                                </div>
                                <div className="space-y-2 max-h-64 overflow-auto">
                                    {actions.map((a, i) => (
                                        <div
                                            key={`act-${a.type}-${i}`}
                                            className="flex items-start gap-2 text-sm border-b-2 border-[#0D2A1B] last:border-b-0 pb-2 last:pb-0"
                                        >
                                            <Check className="w-4 h-4 mt-0.5 shrink-0" />
                                            <div>
                                                <span className="font-bold">{a.type}</span>:{" "}
                                                <span className="text-zinc-700">
                                                    {Object.entries(a.params || {})
                                                        .map(([k, v]) => `${k}=${v}`)
                                                        .join(", ")}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <button
                            data-testid="finish-voice-btn"
                            onClick={onDone}
                            className="brut-btn justify-center text-base"
                        >
                            View my week
                        </button>
                    </div>
                )}
            </div>
        </motion.div>
    );

    async function stopAndProcessFromText(t) {
        setPhase("processing");
        try {
            const res = await voiceOnboard(session.member_id, t);
            const acts = res.parsed_actions || [];
            if (acts.length <= 1) {
                toast.success(res.reply || "Done.");
                onDone?.();
                return;
            }
            setSummary(res.reply || "");
            setActions(acts);
            setPhase("done");
        } catch {
            toast.error("Failed to process");
            setPhase("intro");
        }
    }
}

function TypeInput({ onSubmit }) {
    const [v, setV] = useState("");
    return (
        <div className="mt-3 flex gap-2">
            <input
                data-testid="voice-type-input"
                className="brut-input text-sm"
                placeholder="Describe your week..."
                value={v}
                onChange={(e) => setV(e.target.value)}
            />
            <button
                data-testid="voice-type-submit-btn"
                onClick={() => v.trim() && onSubmit(v.trim())}
                className="brut-btn text-sm"
            >
                Go
            </button>
        </div>
    );
}
